// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useVirtualizer } from "@tanstack/react-virtual";
import { Check, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useRippleDelegated } from "@/hooks/use-ripple";
import { cn } from "@/lib/utils";

const ITEM_HEIGHT = 36;

interface FilterMultiSelectOption {
  label: string;
  value: string;
}

const PILL_STYLE =
  "bg-green-100 text-green-900 border border-green-500/60 dark:bg-green-950 dark:text-green-300 dark:border-green-700";

/**
 * values convention:
 *   null  → all selected (default, no filter active)
 *   Set   → exactly those items are selected (empty Set = nothing selected)
 */
interface FilterMultiSelectProps {
  label: string;
  values: Set<string> | null;
  onChange: (values: Set<string> | null) => void;
  options: FilterMultiSelectOption[];
  searchPlaceholder: string;
  emptyText: string;
  loading?: boolean;
  disabled?: boolean;
}

export function FilterMultiSelect({
  label,
  values,
  onChange,
  options,
  searchPlaceholder,
  emptyText,
  loading,
  disabled,
}: FilterMultiSelectProps) {
  const { t } = useTranslation("back-office");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);

  useRippleDelegated(scrollEl);

  const allSelected = values === null;

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (q === "") return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  useEffect(function resetFocusIndexOnMount() {
    setFocusedIndex(-1);
  }, []);

  const virtualizer = useVirtualizer({
    // +1 for the "All" row
    count: filtered.length + 1,
    getScrollElement: () => scrollEl,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 8,
  });

  function toggle(value: string) {
    if (allSelected) {
      // Switching from "all" → deselect this one item
      const next = new Set(options.map((o) => o.value));
      next.delete(value);
      onChange(next);
      return;
    }
    const next = new Set(values);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    // If all are now selected, go back to null
    if (next.size === options.length) {
      onChange(null);
      return;
    }
    onChange(next);
  }

  function toggleAll() {
    if (allSelected) {
      onChange(new Set());
    } else {
      onChange(null);
    }
  }

  function isSelected(value: string): boolean {
    if (allSelected) return true;
    return values.has(value);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = Math.min(focusedIndex + 1, filtered.length);
      setFocusedIndex(next);
      virtualizer.scrollToIndex(next);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev = Math.max(focusedIndex - 1, 0);
      setFocusedIndex(prev);
      virtualizer.scrollToIndex(prev);
    } else if (e.key === "Enter" && focusedIndex >= 0) {
      e.preventDefault();
      if (focusedIndex === 0 && !search) {
        toggleAll();
      } else {
        const idx = search ? focusedIndex : focusedIndex - 1;
        const item = filtered[idx];
        if (item) toggle(item.value);
      }
    }
  }

  // Display label
  const selectedCount = allSelected ? options.length : values.size;
  const noneSelected = values !== null && values.size === 0;
  const hasSelection = !allSelected && selectedCount > 0;
  const showPill = hasSelection || noneSelected;
  const displayPill = noneSelected
    ? "Nothing selected"
    : hasSelection && selectedCount === 1
      ? options.find((o) => isSelected(o.value))?.label
      : hasSelection
        ? `${selectedCount} selected`
        : null;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setTimeout(() => setSearch(""), 150);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={loading || disabled}
          className={`font-normal gap-1.5 ${showPill ? "bg-sidebar-accent-hover hover:bg-sidebar-accent border-sidebar-accent hover:border-sidebar-primary" : "border-dashed"}`}
        >
          <span className={loading ? "text-muted-foreground" : ""}>{loading ? t("common.loading") : label}</span>
          {displayPill && (
            <>
              <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-sm font-medium ${PILL_STYLE}`}>
                {displayPill}
              </span>
              <button
                type="button"
                className="ml-0.5 text-muted-foreground hover:text-foreground transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(null);
                }}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <X className="h-3 w-3" />
              </button>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-64" align="start" side="bottom" sideOffset={-36}>
        <div className="flex items-center gap-2 px-3 py-2 border-b">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            autoFocus
          />
        </div>
        <div
          ref={setScrollEl}
          className="overflow-y-auto rounded-b-md"
          style={{ height: Math.min((filtered.length + (search ? 0 : 1)) * ITEM_HEIGHT, 220) }}
          onWheel={(e) => e.stopPropagation()}
        >
          {filtered.length === 0 ? (
            <div className="py-4 text-center text-sm text-muted-foreground">{emptyText}</div>
          ) : (
            <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
              {virtualizer.getVirtualItems().map((vItem) => {
                // First item (when not searching) is "All" toggle
                if (vItem.index === 0 && !search) {
                  const isFocused = vItem.index === focusedIndex;
                  return (
                    <div
                      key="__all__"
                      data-ripple
                      style={{ position: "absolute", top: vItem.start, left: 0, right: 0, height: ITEM_HEIGHT }}
                      className={cn(
                        "relative overflow-hidden flex items-center gap-2 px-2 cursor-pointer text-sm font-medium hover:bg-accent border-b",
                        isFocused && "bg-accent",
                      )}
                      onClick={toggleAll}
                    >
                      <span className="flex-1 truncate">All</span>
                      {allSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
                    </div>
                  );
                }

                const optionIndex = search ? vItem.index : vItem.index - 1;
                const option = filtered[optionIndex];
                if (!option) return null;
                const selected = isSelected(option.value);
                const isFocused = vItem.index === focusedIndex;

                return (
                  <div
                    key={option.value}
                    data-ripple
                    style={{ position: "absolute", top: vItem.start, left: 0, right: 0, height: ITEM_HEIGHT }}
                    className={cn(
                      "relative overflow-hidden flex items-center gap-2 px-2 cursor-pointer text-sm hover:bg-accent",
                      isFocused ? "bg-accent" : selected ? "bg-accent/60" : "",
                    )}
                    onClick={() => toggle(option.value)}
                  >
                    <span className="flex-1 truncate">{option.label}</span>
                    {selected && <Check className="h-4 w-4 text-primary shrink-0" />}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
